import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { GenericService } from './generic.service';

type MultipartInitResponse = {
  upload_id: string;
  key: string;
  part_size: number;
};

type MultipartSignPartResponse = {
  url: string;
  method: string;
};

@Injectable({
  providedIn: 'root'
})
export class ArquivosService extends GenericService {

  constructor(http: HttpClient) {
    super(http, 'arquivos');
  }

  uploadEstacao(arquivo: File, descricao: string, estacao_id: number = null, onProgress?: (progress: number) => void): Promise<any> {
    return this.uploadMultipart(arquivo, descricao, 0, estacao_id ?? undefined, undefined, undefined, onProgress);
  }

  destroyEstacao(arquivoId: number): Observable<any> {
    return this.http.delete(`${this.url_base}/${this.prefixo}/estacoes/${arquivoId}`);
  }

  getGrupoEstacao(): Observable<any> {
    return this.http.get(`${this.url_base}/${this.prefixo}/grupo/estacao`);
  }

  getArquivosEstacao(estacaoId: number): Observable<any> {
    return this.http.get(`${this.url_base}/${this.prefixo}/estacao/${estacaoId}`);
  }

  // Nova API - Listar todos os arquivos
  getAll(): Observable<any> {
    return this.http.get(`${this.url_base}/${this.prefixo}`);
  }

  // Buscar documentos por cliente
  getArquivosCliente(clienteId: number): Observable<any> {
    return this.http.get(`${this.url_base}/${this.prefixo}/cliente/${clienteId}`);
  }

  // Estatísticas agrupadas por cliente
  getGrupoCliente(): Observable<any> {
    return this.http.get(`${this.url_base}/${this.prefixo}/grupo/cliente`);
  }

  // Nova API - Criar arquivo (com tipo_documento)
  upload(
    arquivo: File | null,
    descricao: string,
    tipoDocumento: number,
    estacaoId?: number,
    clienteId?: number,
    documentoRepositorioId?: number,
    onProgress?: (progress: number) => void
  ): Promise<any> {
    if (!arquivo) {
      return Promise.reject(new Error('Arquivo é obrigatório para upload multipart.'));
    }

    return this.uploadMultipart(
      arquivo,
      descricao,
      tipoDocumento,
      estacaoId,
      clienteId,
      documentoRepositorioId,
      onProgress
    );
  }

  // Nova API - Atualizar arquivo
  override update(data: any, id: number): Observable<any> {
    return this.http.put(`${this.url_base}/${this.prefixo}/${id}`, data);
  }

  private async uploadMultipart(
    arquivo: File,
    descricao: string,
    tipoDocumento: number,
    estacaoId?: number,
    clienteId?: number,
    documentoRepositorioId?: number,
    onProgress?: (progress: number) => void
  ): Promise<any> {
    const initPayload: Record<string, unknown> = {
      original_name: arquivo.name,
      mime_type: arquivo.type || 'application/octet-stream',
      size: arquivo.size,
      descricao,
      tipo_documento: tipoDocumento,
    };

    if (tipoDocumento === 0 && estacaoId) {
      initPayload['estacao_id'] = estacaoId;
    }

    if (tipoDocumento === 1 && clienteId) {
      initPayload['cliente_id'] = clienteId;
    }

    if (documentoRepositorioId) {
      initPayload['documento_repositorio_id'] = documentoRepositorioId;
    }

    const init = await firstValueFrom(
      this.http.post<MultipartInitResponse>(`${this.url_base}/${this.prefixo}/multipart/initiate`, initPayload)
    );

    const partSize = init.part_size || 10 * 1024 * 1024;
    const totalParts = Math.ceil(arquivo.size / partSize);
    const parts: Array<{ PartNumber: number; ETag: string }> = [];
    let uploadedBytesBeforePart = 0;

    try {
      for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
        const start = (partNumber - 1) * partSize;
        const end = Math.min(start + partSize, arquivo.size);
        const blob = arquivo.slice(start, end);

        const signedPart = await firstValueFrom(
          this.http.post<MultipartSignPartResponse>(`${this.url_base}/${this.prefixo}/multipart/sign-part`, {
            upload_id: init.upload_id,
            key: init.key,
            part_number: partNumber,
          })
        );

        const eTag = await this.uploadPartWithProgress(
          signedPart.url,
          blob,
          arquivo.size,
          uploadedBytesBeforePart,
          onProgress
        );

        parts.push({
          PartNumber: partNumber,
          ETag: eTag,
        });

        uploadedBytesBeforePart += blob.size;
      }

      if (onProgress) {
        onProgress(100);
      }

      return await firstValueFrom(
        this.http.post(`${this.url_base}/${this.prefixo}/multipart/complete`, {
          upload_id: init.upload_id,
          key: init.key,
          parts,
        })
      );
    } catch (error) {
      try {
        await firstValueFrom(
          this.http.post(`${this.url_base}/${this.prefixo}/multipart/abort`, {
            upload_id: init.upload_id,
            key: init.key,
          })
        );
      } catch {
        // Mantém o erro original se o abort também falhar.
      }

      throw error;
    }
  }

  private uploadPartWithProgress(
    url: string,
    blob: Blob,
    totalSize: number,
    uploadedBytesBeforePart: number,
    onProgress?: (progress: number) => void
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url, true);

      xhr.upload.onprogress = (event) => {
        if (!onProgress || !event.lengthComputable) {
          return;
        }

        const loaded = uploadedBytesBeforePart + event.loaded;
        const progress = Math.min(99.9, (loaded / totalSize) * 100);
        onProgress(Number(progress.toFixed(2)));
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const rawETag = xhr.getResponseHeader('ETag') || xhr.getResponseHeader('etag');
          if (!rawETag) {
            reject(new Error('Resposta do S3 sem ETag. Configure o bucket CORS para expor o header ETag.'));
            return;
          }

          resolve(rawETag.replaceAll('"', ''));
          return;
        }

        reject(new Error(`Falha ao enviar parte para o S3. Status ${xhr.status}.`));
      };

      xhr.onerror = () => reject(new Error('Erro de rede ao enviar parte para o S3.'));
      xhr.send(blob);
    });
  }

}
